import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { lazy, Suspense, useEffect } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SvgIconComponent } from '@mui/icons-material';
import SubjectIcon from '@mui/icons-material/Subject';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import ApprovalIcon from '@mui/icons-material/Approval';
import CampaignIcon from '@mui/icons-material/Campaign';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { ADDONS } from '#common/constants/addons';

const CouponBanner = lazy(() => import('#addons/promotions/CouponBanner'));

const BRAND_GOLD = '#d9a258';
const BRAND_GOLD_LIGHT = '#e8b96e';
const BRAND_NAVY = '#32668a';
const BRAND_GOLD_SOFT = 'rgba(217, 162, 88, 0.14)';
const ANNOUNCEMENT_BG = 'rgba(217, 162, 88, 0.08)';

interface CardInfo {
  icon: SvgIconComponent;
  cardKey: string;
  link: string;
}

const CARD_INFO: CardInfo[] = [{
  icon: AddShoppingCartIcon,
  cardKey: 'shopAll',
  link: 'shop',
}, {
  icon: SubjectIcon,
  cardKey: 'myOrders',
  link: 'order',
}, {
  icon: ApprovalIcon,
  cardKey: 'myInvoice',
  link: 'invoices',
}, {
  icon: EmojiPeopleIcon,
  cardKey: 'customerService',
  link: 'customers',
}];

function AnnouncementCard({ html }: { html: string }) {
  const { t } = useTranslation('home');
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        bgcolor: ANNOUNCEMENT_BG,
        borderLeft: `3px solid ${BRAND_GOLD}`,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CampaignIcon sx={{ color: BRAND_GOLD, fontSize: 20 }} />
        <Typography
          sx={{
            color: BRAND_NAVY,
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.12em',
          }}
        >
          {t('announcements')}
        </Typography>
      </Box>
      <Box
        sx={{
          color: 'text.primary',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          wordBreak: 'break-word',
          '& p': { m: 0 },
          '& p + p': { mt: 0.5 },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Box>
  );
}

function Landing() {
  const appConfig = useSelector((state: { appConfig: { announcement?: string } }) => state.appConfig);
  const userInfo = useSelector((state: { auth: { userInfo?: { username?: string } | null } }) => state.auth.userInfo);
  const { t } = useTranslation('home');

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, []);

  const isPromotionsEnabled = window.subscriptions?.[ADDONS.PROMOTIONS]
    && !window.subscriptions[ADDONS.PROMOTIONS]?.expired;

  const announcementNode = appConfig.announcement
    ? <AnnouncementCard html={appConfig.announcement} />
    : null;

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
      }}
    >
      {/* Welcome header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography
          sx={{
            color: BRAND_NAVY,
            fontWeight: 300,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            letterSpacing: '0.008em',
            lineHeight: 1.235,
          }}
        >
          {t('welcomeBack')}
          {userInfo?.username && (
            <>
              ,{' '}
              <Box component="span" sx={{ fontWeight: 500 }}>
                {userInfo.username}
              </Box>
            </>
          )}
        </Typography>
        <Box
          sx={{
            width: 40,
            height: 3,
            borderRadius: 1,
            background: `linear-gradient(90deg, ${BRAND_GOLD_LIGHT} 0%, ${BRAND_GOLD} 100%)`,
            my: 1.25,
          }}
        />
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {t('prompt')}
        </Typography>
      </Box>
      {/* Announcement + coupons */}
      {isPromotionsEnabled ? (
        <Suspense fallback={announcementNode && <Box sx={{ mb: { xs: 2, sm: 3 } }}>{announcementNode}</Box>}>
          <CouponBanner announcement={announcementNode} />
        </Suspense>
      ) : (
        announcementNode && <Box sx={{ mb: { xs: 2, sm: 3 } }}>{announcementNode}</Box>
      )}
      {/* Action tiles */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(2, 1fr)',
          },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {CARD_INFO.map((ci) => (
          <Link key={ci.link} to={ci.link} style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
                transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 3,
                  background: `linear-gradient(180deg, ${BRAND_GOLD_LIGHT} 0%, ${BRAND_GOLD} 100%)`,
                  transform: 'scaleY(0)',
                  transformOrigin: 'top center',
                  transition: 'transform 280ms ease',
                },
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: 4,
                  borderColor: BRAND_GOLD,
                  '&::before': { transform: 'scaleY(1)' },
                  '& .tile-icon': {
                    bgcolor: BRAND_GOLD,
                    color: '#fff',
                  },
                  '& .tile-arrow': {
                    opacity: 1,
                    transform: 'translateX(0)',
                  },
                },
              }}
            >
              <Box
                className="tile-icon"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: BRAND_GOLD_SOFT,
                  color: BRAND_GOLD,
                  flexShrink: 0,
                  transition: 'background-color 220ms ease, color 220ms ease',
                }}
              >
                <ci.icon sx={{ fontSize: 26 }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: BRAND_NAVY,
                    fontWeight: 700,
                    fontSize: '1rem',
                    mb: 0.25,
                  }}
                >
                  {t(`cards.${ci.cardKey}.label`)}
                </Typography>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {t(`cards.${ci.cardKey}.subtitle`)}
                </Typography>
              </Box>

              <ArrowForwardIcon
                className="tile-arrow"
                sx={{
                  fontSize: 18,
                  color: BRAND_GOLD,
                  opacity: 0,
                  transform: 'translateX(-4px)',
                  transition: 'opacity 220ms ease, transform 220ms ease',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              />
            </Box>
          </Link>
        ))}
      </Box>
    </Box>
  );
}

export default Landing;
